import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestAll } from '../../read-all';
import type { AgentDashboardView } from '@forecastify/shared';
import { DashboardChat } from '../../agent/dashboard-chat';
import {
  contractFinanceSchema,
  currencySettingsSchema,
  defaultGranularity,
  type ContractFinance,
  type EacMode,
  type FinanceGranularity,
  type MarginMode,
  type Project,
} from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  LoadingCard,
  LoadingOverlay,
  MultiSelect,
} from '@forecastify/ui';
import { useT } from '../../../i18n';
import { CONTRACT_TOPICS } from '../../../live/topics';
import { useLiveInvalidation } from '../../../live/use-live';
import { useSession } from '../../../session';
import { useWorkspaceNavigate } from '../../../workspace-path';
import { FinanceKpis, BudgetPanel, ProfitabilityPanel } from './finance-panels';
import { FinanceChart } from './finance-chart';
import { PeriodKindCharts } from './period-kind-charts';
import { CostBreakdown, FinanceTable } from './finance-tables';
import { contractTypeLabel } from '../contract-type-label';
import { deriveFinance } from './finance-model';
import { effectiveMode } from '../eac-mode';
import type { Currency } from './finance-format';
import { lookbackMenuItems, type Lookback } from '../../history/use-lookback';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A projekt Pénzügy fülje — C58. `proj-fin.png`, transcribed.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE CONTRACT, OR — SINCE U-4 — THE WHOLE PROJECT.** A project may hold a retainer and a
 * fixed-price build at once, and their PERIOD economics do not add up — one has periods and a run
 * rate, the other a schedule index. So the contract stays the unit of the run-rate tiles, and the
 * *Teljes projekt* option (`GET /projects/:id/finance`) sums every contract plus the hours logged on
 * tasks with no line item, with calendar months for columns and no period panels at all.
 *
 * ⚠️ **The default is the whole project when there is more than one contract**, and the contract
 * when there is one: a single-contract project's whole is that contract plus the stray hours, and
 * opening on the contract keeps its period scoping one click closer.
 *
 * ⚠️ **Every control is a QUERY, and the server recomputes.** Scope, currency and the
 * external-cost switch change what the figures mean, not merely how they are drawn — deriving them
 * client-side would put the same arithmetic in two places, which is the thing `finance.service.ts`
 * exists to prevent.
 */
export function ProjectFinanceTab({
  project,
  asOf = null,
  lookback,
}: {
  readonly project: Project;
  /**
   * C62.1 · ⚠⚠ **THE CUT, PASSED STRAIGHT THROUGH TO THE SERVER.** `null` is live. The whole
   * panel travels because the server recomputes it from rewound rows — nothing here reconstructs
   * anything, which is the point of the temp-table design.
   */
  readonly asOf?: string | null;
  /** C62.4 · ⚠️ the shared cut — Pénzügy enters and leaves it from its own kebab too. */
  readonly lookback?: Lookback;
}) {
  /* 21 Oct 2026 · no client: an internal project, whose income figures are hidden. */
  const internal = project.clientId === null;
  const t = useT();
  const session = useSession();
  const go = useWorkspaceNavigate();

  const [contracts, setContracts] = useState<
    readonly {
      id: string;
      identifier: string;
      name: string;
      type: string;
      startsOn: string | null;
      endsOn: string | null;
    }[]
  >([]);
  const [contractId, setContractId] = useState<string | null>(null);
  /* U-4 · the whole project instead of one contract. Decided once the contracts are known. */
  const [wholeProject, setWholeProject] = useState(false);
  const [view, setView] = useState<ContractFinance | null>(null);
  const [currencies, setCurrencies] = useState<readonly Currency[]>([]);
  const [currencyCode, setCurrencyCode] = useState<string | null>(null);
  /* 30 Oct 2026 · a recurring contract's periods are picked SEVERAL at a time, gaps allowed. */
  const [scope, setScope] = useState<FinanceScope>({});
  const [includeExternal, setIncludeExternal] = useState(true);
  /* 30 Oct 2026 · EV · null until the reader picks — the server's default stands till then. */
  const [pickedEac, setEacMode] = useState<EacMode | null>(null);
  const eacMode = effectiveMode(pickedEac, view?.eac ?? null);
  const [marginMode, setMarginMode] = useState<MarginMode>('forecast');
  /* 16 Oct 2026 · the chart's columns. `null` until the reader picks — the mockup's default then. */
  const [granularity, setGranularity] = useState<FinanceGranularity | null>(null);
  const [failed, setFailed] = useState(false);
  /* 30 Oct 2026 · which look-back day the figures on screen answer, and the newest request's number. */
  const seq = useRef(0);
  const [settledAsOf, setSettledAsOf] = useState<string | null | undefined>(undefined);
  const delta = lookback?.delta ?? false;
  const [liveView, setLiveView] = useState<ContractFinance | null>(null);

  /* The project's contracts, for the first picker. Until they land the tab says it is loading
     rather than "no contract" — `use-first-load.ts`'s rule, by hand because `reload` waits on it. */
  const [contractsLoaded, setContractsLoaded] = useState(false);
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      /* 31 Oct 2026 · the register pages now; this project's contracts are all of them. */
      const response = await requestAll(session, `/contracts?projectId=${project.id}`).catch(
        () => null,
      );
      if (response?.ok !== true) return;
      const body = (await response.json()) as { data?: unknown } | null;
      const rows = Array.isArray(body?.data) ? body.data : [];
      const mine = rows
        .filter(
          (
            one,
          ): one is {
            id: string;
            identifier: string;
            name: string;
            type: string;
            projectId: string;
            startsOn?: string | null;
            endsOn?: string | null;
          } =>
            typeof one === 'object' &&
            one !== null &&
            (one as { projectId?: unknown }).projectId === project.id,
        )
        .map((one) => ({
          id: one.id,
          identifier: one.identifier,
          name: one.name,
          type: one.type,
          startsOn: one.startsOn ?? null,
          endsOn: one.endsOn ?? null,
        }));
      setContracts(mine);
      setContractId((current) => current ?? mine[0]?.id ?? null);
      if (mine.length > 1) setWholeProject(true);
    })().finally(() => {
      setContractsLoaded(true);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, project.id]);

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/workspace/currencies').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = currencySettingsSchema.safeParse(await response.json());
      if (!parsed.success) return;
      setCurrencies(
        parsed.data.currencies
          .filter((one) => one.active)
          .map((one) => ({ code: one.code, symbol: one.symbol })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [session.workspaceId]);

  /**
   * ⚠️ **The mockup's default, from the dates the picker already holds** — a day each up to 40
   * working days, a week up to 200, a month beyond — so the first read already asks for the right
   * columns rather than drawing months and then re-reading.
   */
  const defaultColumns = useMemo((): FinanceGranularity => {
    /* ⚠️ 17 Oct 2026 · a picked line item or period sets the columns by ITS dates — one inside a
       single month read as one month-wide column, and the chart had nothing to draw a line with. */
    const picked =
      wholeProject || view === null
        ? undefined
        : scope.lineItemIds !== undefined
          ? spanOf(
              view.lineItems
                .filter((one) => scope.lineItemIds?.includes(one.id))
                .sort((a, b) => (a.startsOn < b.startsOn ? -1 : 1)),
            )
          : scope.occurrenceStartsOn !== undefined
            ? spanOf(view.periods.filter((one) => scope.occurrenceStartsOn?.includes(one.startsOn)))
            : undefined;
    if (picked !== undefined) return defaultGranularity(picked.startsOn, picked.endsOn);
    const chosen = wholeProject ? contracts : contracts.filter((one) => one.id === contractId);
    const starts = chosen.map((one) => one.startsOn).filter((one): one is string => one !== null);
    const ends = chosen.map((one) => one.endsOn).filter((one): one is string => one !== null);
    if (starts.length === 0 || ends.length !== chosen.length) return 'month';
    return defaultGranularity([...starts].sort()[0] ?? '', [...ends].sort().reverse()[0] ?? '');
  }, [contracts, contractId, wholeProject, view, scope]);
  const columns = granularity ?? defaultColumns;

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) return;
    if (!wholeProject && contractId === null) return;
    const query = new URLSearchParams();
    if (currencyCode !== null) query.set('currency', currencyCode);
    /* ⚠️ A line item or a period belongs to one contract — the whole project has neither. */
    if (!wholeProject && scope.lineItemIds !== undefined) {
      query.set('lineItemId', scope.lineItemIds.join(','));
    }
    if (!wholeProject && scope.occurrenceStartsOn !== undefined) {
      query.set('occurrence', scope.occurrenceStartsOn.join(','));
    }
    if (!includeExternal) query.set('external', 'out');
    query.set('granularity', columns);

    /* C62.1 · the cut travels with every other parameter. Absent is live. */
    if (asOf !== null) query.set('asOf', asOf);
    /**
     * ⚠️⚠️ 30 Oct 2026 · **ONLY THE NEWEST ANSWER IS SHOWN.** Stepping through days fires a
     * request per click, and a slow one landing after a quicker, newer one put the wrong day's
     * figures under the scrubber. Each reload takes a number; an answer to an older number is
     * dropped. `settledAsOf` then says which day the figures on screen are for.
     */
    seq.current += 1;
    const mine = seq.current;
    const asked = asOf;

    const path = (params: URLSearchParams): string =>
      wholeProject
        ? `/projects/${project.id}/finance?${params.toString()}`
        : `/contracts/${contractId ?? ''}/finance?${params.toString()}`;
    /**
     * 30 Oct 2026 · ⚠️ **ELTÉRÉS A JELENHEZ — TODAY'S FIGURES, READ ALONGSIDE THE PAST ONES.** The
     * kebab's toggle was wired to nothing; the mockup writes today's value under each tile, and
     * today's value is this same read without the cut.
     */
    const today = new URLSearchParams(query);
    today.delete('asOf');
    const [response, live] = await Promise.all([
      session.request(path(query)).catch(() => null),
      asOf !== null && delta ? session.request(path(today)).catch(() => null) : null,
    ]);
    const body = response?.ok === true ? ((await response.json()) as unknown) : null;
    const liveBody = live?.ok === true ? ((await live.json()) as unknown) : null;
    if (mine !== seq.current) return;
    setSettledAsOf(asked);
    const liveParsed = contractFinanceSchema.safeParse(liveBody);
    setLiveView(liveParsed.success ? liveParsed.data : null);
    if (body === null) {
      setFailed(true);
      return;
    }
    const parsed = contractFinanceSchema.safeParse(body);
    setFailed(!parsed.success);
    if (parsed.success) setView(parsed.data);
    /* ⚠️ `asOf` in the deps, or moving the scrubber would leave the tab on the previous day. */
  }, [
    session,
    contractId,
    wholeProject,
    project.id,
    currencyCode,
    scope,
    includeExternal,
    asOf,
    columns,
    delta,
  ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    void reload();
  });

  const model = useMemo(
    () => (view === null ? null : deriveFinance(view, { eacMode })),
    [view, eacMode],
  );
  const liveModel = useMemo(
    () => (liveView === null ? null : deriveFinance(liveView, { eacMode })),
    [liveView, eacMode],
  );

  /**
   * 30 Oct 2026 · ⚠️ **NOTHING IN VIEW HAS A BUDGET.** A time-and-material line item is billed by
   * the hour and has no frame, so when every item the picked scope covers is one, the figures
   * measured against a budget measure against nothing (4D Soft-ONE on UAT): the budget tiles, the
   * utilisation panel and the chart's budget view go. A period covers the whole contract's items;
   * a picked line item, only itself.
   */
  const noBudget = useMemo(() => {
    if (view === null) return false;
    const items =
      !wholeProject && scope.lineItemIds !== undefined
        ? view.lineItems.filter((item) => scope.lineItemIds?.includes(item.id))
        : view.lineItems;
    return items.length > 0 && items.every((item) => item.type === 'time_and_material');
  }, [view, scope, wholeProject]);

  const currency: Currency = useMemo(() => {
    const code = view?.currencyCode ?? currencyCode ?? 'HUF';
    return currencies.find((one) => one.code === code) ?? { code, symbol: code };
  }, [currencies, view, currencyCode]);

  /* No workspace is never going to answer, so it has nothing to wait for. */
  if (!contractsLoaded && session.workspaceId !== null) {
    return (
      <p className="text-muted-foreground p-6 text-center text-[12.5px]">{t('state.loading')}</p>
    );
  }

  if (contracts.length === 0) {
    return (
      <EmptyState
        label={t('finance.empty.contractLabel')}
        title={t('finance.empty.noContract')}
        body={t('finance.empty.noContractBody')}
      />
    );
  }

  const contract = contracts.find((one) => one.id === contractId) ?? contracts[0];

  /* 19 Oct 2026 · AG-1 · what the dashboard chat is told the person is looking at. */
  const chatView: AgentDashboardView = {
    tab: 'finance',
    ...(wholeProject || contractId === null ? {} : { contractId }),
    /* The agent's tools read ONE line item — with several picked it is told the whole contract. */
    ...(wholeProject || scope.lineItemIds?.length !== 1 || scope.lineItemIds[0] === undefined
      ? {}
      : { lineItemId: scope.lineItemIds[0] }),
    /* 30 Oct 2026 · the agent's tools read ONE period — with several picked it is told the whole
       contract rather than a wrong one. */
    ...(wholeProject ||
    scope.occurrenceStartsOn?.length !== 1 ||
    scope.occurrenceStartsOn[0] === undefined
      ? {}
      : { occurrenceStartsOn: scope.occurrenceStartsOn[0] }),
    ...(asOf === null ? {} : { asOf }),
    eacMode,
    granularity: columns,
    includeExternal,
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
      <div
        className="bg-background sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b px-4 py-2"
        data-control-row
      >
        <DropdownMenu
          className="phone:order-1"
          width={340}
          align="start"
          label={t('finance.pick.contract')}
          items={[
            /* U-4 · the whole project, above its contracts. */
            {
              label: t('finance.scope.project'),
              description: t('finance.scope.projectHint'),
              checked: wholeProject,
              icon: 'layout-grid' as const,
              onSelect: () => {
                setWholeProject(true);
                setScope({});
              },
            },
            { type: 'separator' as const },
            ...contracts.map((one) => ({
              /* 3 Nov 2026 · the NAME leads; the identifier rides under it, after the kind. */
              label: one.name,
              /* ⚠️⚠️ **THE KIND, UNDER THE NAME — the Dashboard picker has carried it since
               round 5 item 12 and this one never did.** It is the fact that decides what the rest
               of the screen offers: a recurring contract has periods to scope by, a milestone one
               has none. Two pickers over the same list saying different things is worse than
               neither saying it. */
              description: `${contractTypeLabel(t, one.type)} · ${one.identifier}`,
              checked: !wholeProject && one.id === contract?.id,
              icon: 'file-text' as const,
              onSelect: () => {
                setWholeProject(false);
                setContractId(one.id);
                /* ⚠️ The scope is CLEARED with the contract — a line item id from the previous one
                 would narrow to nothing and the screen would read as an empty contract. */
                setScope({});
              },
            })),
          ]}
        >
          <Button variant="outline" size="sm" className="gap-1.5">
            <Icon
              name={wholeProject ? 'layout-grid' : 'file-text'}
              size={13}
              className="opacity-70"
            />
            {wholeProject ? t('finance.scope.project') : (contract?.identifier ?? '–')}
            <span className="text-muted-foreground text-[11px]">
              {!wholeProject && contract?.type === 'recurring' ? t('contract.type.recurring') : ''}
            </span>
            <Icon name="chevron-down" size={13} className="opacity-60" />
          </Button>
        </DropdownMenu>

        {view === null ? null : (
          <>
            {view.level === 'project' ? null : view.recurring ? (
              /* 30 Oct 2026 · several periods at once, gaps allowed: a box per row, Shift+click for
                 a run, a search, and a list capped at the kit's height rather than the screen's. */
              <div className="phone:order-4 w-[280px] max-w-full">
                <MultiSelect
                  label={t('finance.pick.scope')}
                  placeholder={t('finance.scope.whole')}
                  options={view.periods.map((period) => ({
                    value: period.startsOn,
                    label: `#${String(period.index)} · ${period.startsOn} – ${period.endsOn}`,
                  }))}
                  values={scope.occurrenceStartsOn ?? []}
                  onValuesChange={(values) => {
                    setScope(values.length === 0 ? {} : { occurrenceStartsOn: values });
                  }}
                  checkboxes
                  rangeSelect
                  searchable
                  searchPlaceholder={t('finance.scope.periodSearch')}
                  emptyLabel={t('finance.scope.periodNone')}
                  hint={t('finance.scope.periodHint')}
                  clearLabel={t('finance.scope.whole')}
                  panelWidth={340}
                  summarize={() => (
                    <>
                      <Icon
                        name={
                          scope.occurrenceStartsOn === undefined ? 'layout-grid' : 'calendar-days'
                        }
                        size={13}
                        className="shrink-0 opacity-70"
                      />
                      <span className="truncate">{scopeLabel(view, scope, t)}</span>
                    </>
                  )}
                />
              </div>
            ) : (
              /* 30 Oct 2026 · several line items at once, as a recurring contract's periods. */
              <div className="phone:order-4 w-[280px] max-w-full">
                <MultiSelect
                  label={t('finance.pick.scope')}
                  placeholder={t('finance.scope.whole')}
                  options={view.lineItems.map((item) => ({ value: item.id, label: item.name }))}
                  values={scope.lineItemIds ?? []}
                  onValuesChange={(values) => {
                    setScope(values.length === 0 ? {} : { lineItemIds: values });
                  }}
                  checkboxes
                  rangeSelect
                  searchable
                  searchPlaceholder={t('finance.scope.itemSearch')}
                  emptyLabel={t('finance.scope.itemNone')}
                  hint={t('finance.scope.itemHint')}
                  clearLabel={t('finance.scope.whole')}
                  panelWidth={340}
                  summarize={() => (
                    <>
                      <Icon
                        name={scope.lineItemIds === undefined ? 'layout-grid' : 'list'}
                        size={13}
                        className="shrink-0 opacity-70"
                      />
                      <span className="truncate">{scopeLabel(view, scope, t)}</span>
                    </>
                  )}
                />
              </div>
            )}

            <DropdownMenu
              className="phone:order-5"
              width={260}
              align="start"
              label={t('finance.pick.currency')}
              items={[
                {
                  /* ⚠️ On the whole project "its own currency" is the workspace default — the
                     contracts may each be agreed in a different one. */
                  label:
                    view.level === 'project'
                      ? t('finance.currency.workspace')
                      : t('finance.currency.contract'),
                  description:
                    view.level === 'project'
                      ? t('finance.currency.workspaceHint')
                      : t('finance.currency.contractHint'),
                  checked: currencyCode === null,
                  onSelect: () => {
                    setCurrencyCode(null);
                  },
                },
                { type: 'separator' as const },
                ...currencies.map((one) => ({
                  label: one.code,
                  checked: currencyCode === one.code,
                  onSelect: () => {
                    setCurrencyCode(one.code);
                  },
                })),
              ]}
            >
              <Button variant="outline" size="sm" className="gap-1.5">
                <Icon name="credit-card" size={13} className="opacity-70" />
                <span className="text-[11.5px] tabular-nums">{currency.code}</span>
                <Icon name="chevron-down" size={13} className="opacity-60" />
              </Button>
            </DropdownMenu>

            {/* 17 Oct 2026 · on a phone the row breaks here: ⋯ stays beside the contract, the scope
                and the currency go to the second line. */}
            <div className="hidden basis-full phone:order-3 phone:block" />
            <div className="flex-1 phone:hidden" />

            <DropdownMenu
              className="phone:order-2 phone:ml-auto"
              width={260}
              align="end"
              label={t('finance.pick.more')}
              items={[
                /* ⚠️ C62.4 · the same two menus as the Dashboard's kebab — one cut, entered and
                   left from whichever tab the reader happens to be on. */
                ...(lookback === undefined ? [] : lookbackMenuItems(t, lookback)),
                ...(view.level === 'project'
                  ? []
                  : [
                      { type: 'separator' as const },
                      {
                        label: t('finance.action.openContract'),
                        icon: 'file-text' as const,
                        disabled: lookback?.onDate !== undefined && lookback.onDate !== null,
                        ...(lookback?.onDate === undefined || lookback.onDate === null
                          ? {}
                          : { description: t('tt.lockedAction') }),
                        onSelect: () => {
                          go(`/contracts/${view.contractIdentifier}`);
                        },
                      },
                    ]),
              ]}
            >
              <Button variant="ghost" size="icon-sm" aria-label={t('finance.pick.more')}>
                <Icon name="ellipsis" size={15} />
              </Button>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* 30 Oct 2026 · the body under one roof, so a picked day's scrim covers all of it. */}
      <div className="relative flex flex-col">
        <LoadingOverlay
          show={view !== null && settledAsOf !== undefined && settledAsOf !== asOf}
          label={asOf === null ? t('tt.loadingLive') : t('tt.loading', { date: asOf.slice(0, 10) })}
          slowNote={asOf === null ? undefined : t('tt.slow')}
        />
        {failed ? (
          <p className="text-destructive px-4 pt-3 text-[12px]">{t('error.unavailable')}</p>
        ) : null}

        {(view === null || model === null) && asOf !== null && !failed ? (
          /* 3 Nov 2026 · a past day opened directly is the same slow rebuild as one picked. */
          <div className="flex justify-center p-6">
            <LoadingCard
              label={t('tt.loading', { date: asOf.slice(0, 10) })}
              slowNote={t('tt.slow')}
            />
          </div>
        ) : view === null || model === null ? (
          <p className="text-muted-foreground p-6 text-center text-[12.5px]">
            {t('state.loading')}
          </p>
        ) : view.lineItems.length === 0 ? (
          <EmptyState
            label={t('finance.empty.lineItemLabel')}
            title={t('finance.empty.noLineItem')}
            body={t('finance.empty.noLineItemBody')}
          />
        ) : (
          <div className="flex flex-col gap-4 p-4">
            {/**
             * ⚠⚠ **THE SCREEN ADMITS WHEN ITS TOTALS ARE APPROXIMATE.** A currency the workspace
             * cannot price against the display currency is added at 1:1 rather than dropped — the
             * lesser of two wrongs, because dropping it makes cost too low and margin too good. What
             * was missing was any sign of it: 320 EUR entering a HUF total as 320 Ft moves nothing
             * visibly and is wrong by a factor of four hundred.
             *
             * ⚠️ At the TOP, not inside a panel. It is a statement about every figure below it, and
             * the one place it must not sit is next to the one number somebody happens to be reading.
             */}
            {view.unconvertedCurrencies.length === 0 ? null : (
              <p className="border-destructive/40 bg-destructive/5 text-foreground rounded-lg border px-3 py-2 text-[12px]">
                {t('finance.fx.unconverted', {
                  codes: view.unconvertedCurrencies.join(', '),
                  currency: view.currencyCode,
                })}
              </p>
            )}

            <FinanceKpis
              view={view}
              model={model}
              currency={currency}
              eacMode={eacMode}
              marginMode={marginMode}
              onEacMode={setEacMode}
              onMarginMode={setMarginMode}
              internal={internal}
              noBudget={noBudget}
              live={
                asOf !== null && delta && liveView !== null && liveModel !== null
                  ? { view: liveView, model: liveModel }
                  : null
              }
            />

            <div
              className={
                internal || noBudget
                  ? 'grid grid-cols-1 gap-4'
                  : 'grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]'
              }
            >
              {noBudget ? null : (
                <BudgetPanel
                  view={view}
                  model={model}
                  currency={currency}
                  eacMode={eacMode}
                  includeExternal={includeExternal}
                  onEacMode={setEacMode}
                  onIncludeExternal={setIncludeExternal}
                />
              )}
              {internal ? null : (
                <ProfitabilityPanel view={view} model={model} currency={currency} />
              )}
            </div>

            <FinanceChart
              view={view}
              currency={currency}
              eacMode={eacMode}
              /* ⚠️ A recurring contract's own columns are its periods — no switch, as in the mockup.
               The whole project is calendar columns, so it has one (decision f). */
              granularity={view.recurring && view.level === 'contract' ? null : columns}
              onGranularity={setGranularity}
              internal={internal}
              noBudget={noBudget}
            />

            {/* 30 Oct 2026 · a recurring contract's periods split by kind of work — the legacy
              budget-difference and profit charts. Not on one picked period: a single column. */}
            {view.recurring &&
            view.level === 'contract' &&
            !internal &&
            (wholeProject || (scope.occurrenceStartsOn?.length ?? 0) !== 1) ? (
              <PeriodKindCharts
                view={view}
                currency={currency}
                showBudget={!noBudget}
                kinds={
                  new Set(
                    view.lineItems
                      .filter(
                        (item) =>
                          wholeProject ||
                          scope.lineItemIds === undefined ||
                          scope.lineItemIds.includes(item.id),
                      )
                      .map((item) => item.type),
                  )
                }
              />
            ) : null}

            <FinanceTable
              view={view}
              currency={currency}
              scope={scope}
              includeExternal={includeExternal}
              internal={internal}
              onOpenPeriod={(startsOn) => {
                go(`/contracts/${view.contractIdentifier}/periods/${startsOn}`);
              }}
              onOpenLineItem={(id) => {
                const item = view.lineItems.find((one) => one.id === id);
                /* ⚠️ The ROW's contract — on the whole project the view's own identity is the
                 project's, and the rows come from several contracts. */
                if (item !== undefined)
                  go(`/contracts/${item.contractIdentifier}/${item.identifier}`);
              }}
            />

            <CostBreakdown view={view} model={model} currency={currency} />
          </div>
        )}
      </div>

      <DashboardChat projectId={project.id} view={chatView} />
    </div>
  );
}

/** 30 Oct 2026 · what the money tab is scoped to — a line item, or any number of periods. */
export interface FinanceScope {
  readonly lineItemIds?: readonly string[];
  readonly occurrenceStartsOn?: readonly string[];
}

/** The first start and the last end of some periods — the columns' default reads their span. */
function spanOf(
  periods: readonly { startsOn: string; endsOn: string }[],
): { startsOn: string; endsOn: string } | undefined {
  const first = periods[0];
  const last = periods[periods.length - 1];
  return first === undefined || last === undefined
    ? undefined
    : { startsOn: first.startsOn, endsOn: last.endsOn };
}

function scopeLabel(
  view: ContractFinance,
  scope: FinanceScope,
  t: ReturnType<typeof useT>,
): string {
  if (scope.occurrenceStartsOn !== undefined) {
    const picked = view.periods.filter((one) => scope.occurrenceStartsOn?.includes(one.startsOn));
    const first = picked[0];
    if (first === undefined) return t('finance.scope.whole');
    if (picked.length === 1) {
      return `#${String(first.index)} · ${first.startsOn} – ${first.endsOn}`;
    }
    /* 30 Oct 2026 · several: their numbers, and a count past three. */
    return picked.length <= 3
      ? picked.map((one) => `#${String(one.index)}`).join(', ')
      : t('finance.scope.periodsMany', { count: picked.length });
  }
  if (scope.lineItemIds !== undefined) {
    const picked = view.lineItems.filter((one) => scope.lineItemIds?.includes(one.id));
    const first = picked[0];
    if (first === undefined) return t('finance.scope.whole');
    /* 30 Oct 2026 · several: the first name and how many more. */
    return picked.length === 1
      ? first.name
      : t('finance.scope.itemsMany', { name: first.name, count: picked.length - 1 });
  }
  return t('finance.scope.whole');
}

/**
 * ⚠️ **The two empty states name what is MISSING as a big zero**, which is the mockup's own shape —
 * a finance tab with nothing to show should say *"SZERZŐDÉS 0"* rather than draw eight tiles of
 * dashes that look like a broken screen.
 */
function EmptyState({
  label,
  title,
  body,
}: {
  readonly label: string;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-8">
      <div className="max-w-[420px] text-center">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {label}
        </div>
        <div className="mt-1 text-[32px] leading-none font-medium tabular-nums">0</div>
        <div className="mt-3 text-[13px] font-medium">{title}</div>
        <p className="text-muted-foreground mt-1 text-[12.5px] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
